import { useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tooltip,
} from "@mui/material";
import { GroupAdd } from "@mui/icons-material";
import { toast } from "react-toastify";
import { TELECONSULTORIA_GUEST_ALREADY_INVITED } from "../constants";
import { applyTeleconsultoriaInvite } from "../services/applyTeleconsultoriaInvite";
import type { TeleconsultoriaActor, TeleconsultoriaGuest } from "../types";
import { teleconsultoriaGuestOfAppointment } from "../utils/actors";
import { TeleconsultoriaProfessionalPicker } from "./TeleconsultoriaProfessionalPicker";

interface InviteTeleconsultoriaButtonProps {
  appointment?: fhir5.Appointment;
  encounterId?: string;
  onRoomReady: (roomUrl: string) => void;
}

export function InviteTeleconsultoriaButton({
  appointment,
  encounterId,
  onRoomReady,
}: InviteTeleconsultoriaButtonProps) {
  const [open, setOpen] = useState(false);
  const [guest, setGuest] = useState<TeleconsultoriaGuest | null>(null);
  const [saving, setSaving] = useState(false);
  // A prop `appointment` não é atualizada após o convite: guarda o convidado aqui.
  const [invitedGuest, setInvitedGuest] = useState<TeleconsultoriaActor | null>(
    null,
  );
  // O serviço recusou porque já havia outro teleconsultor (tela desatualizada).
  const [blockedByServer, setBlockedByServer] = useState(false);

  // Só 1 teleconsultor por evolução: o já gravado ou o convidado agora.
  const currentGuest =
    invitedGuest || teleconsultoriaGuestOfAppointment(appointment).practitioner;
  const hasGuest = Boolean(currentGuest) || blockedByServer;
  const blockedTooltip = currentGuest?.display
    ? `Teleconsultor já convidado: ${currentGuest.display}`
    : TELECONSULTORIA_GUEST_ALREADY_INVITED;

  const handleInvite = async () => {
    if (!appointment?.id || !guest) return;
    setSaving(true);
    try {
      const updated = await applyTeleconsultoriaInvite({
        appointment,
        guest,
        encounterId,
      });
      const roomUrl = updated.virtualService?.[0]?.addressUrl;
      if (!roomUrl) throw new Error("Sala ausente");
      onRoomReady(roomUrl);
      setInvitedGuest(guest.practitioner);
      toast.success("Profissional convidado para a teleconsultoria.");
      setOpen(false);
      setGuest(null);
    } catch (error) {
      console.error("InviteTeleconsultoriaButton", error);
      if (
        error instanceof Error &&
        error.message === TELECONSULTORIA_GUEST_ALREADY_INVITED
      ) {
        toast.warning(TELECONSULTORIA_GUEST_ALREADY_INVITED);
        setBlockedByServer(true);
        setOpen(false);
        setGuest(null);
        return;
      }
      toast.error("Não foi possível convidar o profissional.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      {/* span: o Tooltip precisa de um wrapper quando o botão está desabilitado. */}
      <Tooltip title={hasGuest ? blockedTooltip : ""}>
        <span>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<GroupAdd fontSize="small" />}
            onClick={() => setOpen(true)}
            disabled={hasGuest}
            sx={{ textTransform: "none", borderRadius: 2, minHeight: 38 }}
          >
           Teleconsultoria
          </Button>
        </span>
      </Tooltip>
      {open && (
        <Dialog
          open={open}
          onClose={() => {
            if (!saving) setOpen(false);
          }}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle>Selecionar teleconsultor</DialogTitle>
          <DialogContent dividers>
            <TeleconsultoriaProfessionalPicker
              selected={guest}
              onSelect={setGuest}
            />
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setOpen(false)}
              disabled={saving}
              sx={{ textTransform: "none" }}
            >
              Cancelar
            </Button>
            <Button
              variant="contained"
              onClick={() => void handleInvite()}
              disabled={!guest || saving}
              sx={{ textTransform: "none" }}
            >
              {saving ? "Convidando..." : "Convidar"}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
}
